import { transaction } from '@src/slide/domain/slide-app';
import _set from 'lodash/set';
import _isNil from 'lodash/isNil';
import { FAST_LAYOUT_CONFIG } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-fast-layout/fast-layout-config';
import ChartDefaultValueFactory from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/default-value-factory';
import { IBarChart, IOfPieChart, ISubChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart';
import { STBarGrouping, SubChartType } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import type {
    IChartLayoutConfig,
    IChartUIFastLayout,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-fast-layout/fast-layout-config';
import type { IChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-model.interface';
import { BarChart } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/bar-chart';
import { IChartLegend } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import { GridlinesType } from '@src/slide/domain/ui-model/element-modeles/constant/chart';
import { IChartSeries } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart/sub-chart-properties.interface';
import { DataListItem } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/series';
import { ISeriesProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/series/series.interface';
import { getAvailableDataLabelPositions } from '@src/slide/domain/ui-model/element-modeles/chart-models/utils/data-label';

/**
 * 负责更新当前chart元素的布局数据。
 * 每个layout对象和chartModel一一对应
 */
export default class ChartFastLayout {
    /**
     * 根据图表类型，获取布局配置信息
     * @param chartType
     * @param subChartType
     */
    public static getChartLayoutConfigs(chartType: SubChartType, subChartType?: string): IChartUIFastLayout[] {
        for (const layoutItem of FAST_LAYOUT_CONFIG) {
            const { type, subType, layouts } = layoutItem;
            if (type === chartType) {
                if (!subChartType || (subChartType && subChartType === subType)) {
                    return layouts;
                }
            }
        }
        return [];
    }

    /**
     * 获取图表类型和子类型。
     * 如果是组合图，取第一个图表类型
     */
    public static getChartType(chartModel: IChartModel): { type: SubChartType; subType?: string } {
        const { charts } = chartModel.plotArea || {};
        const subChart = this.getRightChart(charts);
        let chartType = subChart.type;
        let subType;
        switch (chartType) {
            case SubChartType.BarChart:
            case SubChartType.Bar3DChart: {
                const barChart = subChart as IBarChart;
                subType = String(barChart.direction);
                const group = barChart.grouping;
                if (group === STBarGrouping.stacked || group === STBarGrouping.percentStacked) {
                    subType += '-stack';
                }
                break;
            }
            case SubChartType.BubbleChart:
            case SubChartType.ScatterChart:
                chartType = SubChartType.BubbleChart;
                break;
            case SubChartType.OfPieChart: {
                const ofPieChart = subChart as IOfPieChart;
                subType = String(ofPieChart.ofPieType);
            }
        }
        return { type: chartType, subType };
    }

    /**
     * 组合图时，获取生效的图表配置。默认为第一个图表
     * @param charts
     */
    private static getRightChart(charts: Array<ISubChartModel>): ISubChartModel {
        let subChart = charts[0];
        charts.forEach((chart) => {
            if (chart.type === SubChartType.StockChart) {
                subChart = chart;
            }
        });
        return subChart;
    }

    private readonly chartModel: IChartModel;
    private readonly type: { type: SubChartType; subType?: string };

    public constructor(chartModel: IChartModel) {
        this.chartModel = chartModel;
        this.type = ChartFastLayout.getChartType(chartModel);
    }

    public getCurChartModel(): IChartModel {
        return this.chartModel;
    }

    /**
     * 获取图表类型和子类型。
     * 如果是组合图，取第一个图表类型
     */
    public getType(): { type: SubChartType; subType?: string } {
        return this.type;
    }

    /**
     * 更改当前chart元素的布局。
     * 多种属性变更组合而成
     * 原则：
     * 1. 配置中有的信息进行添加和更新
     * 2. 配置中没有的信息进行移除
     * @param config
     */
    public updateLayout(config: IChartLayoutConfig): void {
        transaction(() => {
            this.updateTitleLayout(config);
            this.updateLegendLayout(config);
            this.updateAxesLayout(config);
            this.updateAxesGridLineLayout(config);
            this.updateLabelsLayout(config);
            this.updateOfLieLayout(config);
            switch (this.type.type) {
                case SubChartType.BarChart:
                case SubChartType.Bar3DChart:
                    this.updateBarConfig(config);
                    break;
                default:
                    break;
            }
        });
    }

    /**
     * 修改chart标题布局数据
     * @param config
     */
    private updateTitleLayout(config: IChartLayoutConfig) {
        const { chartProperty } = this.chartModel;
        const titleProperty = chartProperty.chartTitle;
        if (config.showTitle) {
            if (titleProperty) {
                titleProperty.updateProps(
                    { overlay: false },
                    { layout: null },
                );
            } else {
                chartProperty.createTitle();
            }
        } else {
            if (titleProperty) {
                chartProperty.removeTitle();
            }
        }
    }

    /**
     * 修改chart标题布局数据
     * @param config
     */
    private updateLegendLayout(config: IChartLayoutConfig) {
        const { chartProperty } = this.chartModel;
        const legendProperty = chartProperty.legend;
        if (config.showLegend) {
            const pos = config.legendPos;
            if (legendProperty) {
                const updateProps: IChartLegend = { overlay: false };
                if (pos) updateProps.position = pos;
                legendProperty.updateProps(
                    updateProps,
                    { layout: null },
                );
            } else {
                const useProps: IChartLegend = { };
                if (pos) useProps.position = pos;
                chartProperty.createLegend(useProps);
            }
        } else {
            if (legendProperty) {
                legendProperty.removeLegend();
            }
        }
    }

    /**
     * 处理坐标轴的布局处理
     * @param config
     */
    private updateAxesLayout(config: IChartLayoutConfig) {
        const { chartProperty } = this.chartModel;
        const axesProperty = chartProperty.axes;
        const axes = axesProperty?.getAllAxes();
        const [
            primaryAxisX,
            primaryAxisY,
            secondaryAxisX,
            secondaryAxisY,
        ] = axes || [];
        // 1.处理轴坐标轴的修改
        let isDelete = !config.showXAxes;
        primaryAxisX?.updateProps({ isDelete });
        // secondaryAxisX?.updateProps({ isDelete }); // 次轴显示可能会导致和主轴重叠
        isDelete = !config.showYAxes;
        primaryAxisY?.updateProps({ isDelete });
        secondaryAxisY?.updateProps({ isDelete });
        // 2.处理坐标轴标题的修改。只影响主轴
        const primaryTitleX = primaryAxisX?.title;
        if (config.showXAxesTitle) {
            if (!primaryTitleX) {
                primaryAxisX?.createAxisTitle();
            }
        } else {
            if (primaryTitleX) {
                primaryAxisX?.removeAxisTitle();
            }
        }
        const primaryTitleY = primaryAxisY?.title;
        if (config.showYAxesTitle) {
            if (!primaryTitleY) {
                primaryAxisY?.createAxisTitle();
            }
        } else {
            if (primaryTitleY) {
                primaryAxisY?.removeAxisTitle();
            }
        }
    }

    /**
     * 处理主轴，主网格线和次网格线的显示隐藏
     * @param config
     */
    /* eslint-disable complexity */
    private updateAxesGridLineLayout(config: IChartLayoutConfig) {
        const { chartProperty } = this.chartModel;
        const axesProperty = chartProperty.axes;
        const axes = axesProperty?.getAllAxes();
        const [
            primaryAxisX,
            primaryAxisY,
        ] = axes || [];
        // X轴主网格线
        if (config.showXMajorGridLine) {
            if (!primaryAxisX?.majorGridlines) {
                primaryAxisX?.createGridlines([GridlinesType.MAJOR_GRID]);
            }
        } else {
            if (primaryAxisX?.majorGridlines) {
                primaryAxisX?.removeGridlines([GridlinesType.MAJOR_GRID]);
            }
        }
        // X轴次网格线
        if (config.showXMinorGridLine) {
            if (!primaryAxisX?.minorGridlines) {
                primaryAxisX?.createGridlines([GridlinesType.MINOR_GRID]);
            }
        } else {
            if (primaryAxisX?.minorGridlines) {
                primaryAxisX?.removeGridlines([GridlinesType.MINOR_GRID]);
            }
        }
        // y轴主网格线
        if (config.showYMajorGridLine) {
            if (!primaryAxisY?.majorGridlines) {
                primaryAxisY?.createGridlines([GridlinesType.MAJOR_GRID]);
            }
        } else {
            if (primaryAxisY?.majorGridlines) {
                primaryAxisY?.removeGridlines([GridlinesType.MAJOR_GRID]);
            }
        }
        // y轴次网格线
        if (config.showYMinorGridLine) {
            if (!primaryAxisY?.minorGridlines) {
                primaryAxisY?.createGridlines([GridlinesType.MINOR_GRID]);
            }
        } else {
            if (primaryAxisY?.minorGridlines) {
                primaryAxisY?.removeGridlines([GridlinesType.MINOR_GRID]);
            }
        }
    }

    /**
     * 处理数据标签的显示隐藏以及位置
     * @param config
     */
    private updateLabelsLayout(config: IChartLayoutConfig) {
        if (config.showLabel || config.showLastSerLabel || config.showLastCateLabel) {
            const { charts = [] } = this.chartModel.plotArea;
            charts.forEach((chart: ISubChartModel) => {
                const subChartProperty = chart.getSubChartProperty();
                // 组合图下只设置该子图支持的label position, 否则走默认逻辑
                const labelPos = getAvailableDataLabelPositions(chart.getPresetChartType()).includes(config.labelPos!)
                    ? config.labelPos : undefined;
                const { series = [] } = subChartProperty;
                const updatePropsList: any[] = [];
                const removePropsList: any[] = [];
                series.forEach((ser: ISeriesProperty, index) => {
                    this.fillLabelUpdateAndRemoveProps(
                        ser, index, series.length,
                        {
                            ...config,
                            labelPos,
                        }, updatePropsList, removePropsList,
                    );
                });
                subChartProperty.updateProps(
                    { series: updatePropsList },
                    { series: removePropsList },
                );
            });
        } else {
            this.chartModel.chartProperty.removeDataLabels();
        }
    }

    /**
     * 填充系列中label需要更新和移除的属性
     * @param ser
     * @param index
     * @param count
     * @param config
     * @param updatePropsList
     * @param removePropsList
     */
    private fillLabelUpdateAndRemoveProps(
        ser: ISeriesProperty,
        index,
        count,
        config,
        updatePropsList: any[],
        removePropsList: any[],
    ) {
        // showLastSer和showLastCate互斥
        const showLastSer = config.showLastSerLabel;
        const showLastCate = !showLastSer && config.showLastCateLabel;
        const showAllLabel = !showLastSer && !showLastCate;
        const commonLabelProps = this.createLabelCommonProps(config);

        const updateProps: any = {};
        const removeProps: any = {};
        const { dataLabels, dataList = [] } = ser;
        if (showAllLabel) {
            this.updateSerLabelShowProps(ser, commonLabelProps, updateProps, removeProps);
        } else if (showLastSer) {
            const isLastSer = index === count - 1;
            if (isLastSer) {
                this.updateSerLabelShowProps(ser, commonLabelProps, updateProps, removeProps);
            } else {
                removeProps.dataLabels = null;
                removeProps.dataList = new Array(dataList.length).fill({
                    dataLabel: null,
                });
            }
        } else if (showLastCate) {
            if (!dataLabels) {
                updateProps.dataLabels = ChartDefaultValueFactory.createDataLabels(commonLabelProps);
            } else {
                updateProps.dataLabels = commonLabelProps;
            }
            const labelUpdatePropsList: any[] = [];
            const labelRemovePropsList: any[] = [];
            dataList.forEach((item: DataListItem, index) => {
                const isLastCate = index === dataList.length - 1;
                const { dataLabel } = item;
                if (isLastCate) {
                    if (dataLabel) {
                        labelUpdatePropsList.push({ dataLabel: commonLabelProps });
                    } else {
                        labelUpdatePropsList.push({});
                    }
                    labelRemovePropsList.push({ dataLabel: { layout: null } });
                } else {
                    // 不显示的label
                    if (dataLabel) {
                        const showEle = {
                            showVal: false,
                            showCategoryName: false,
                            showSeriesName: false,
                            showPercent: false,
                            showLegendKey: false,
                        };
                        labelUpdatePropsList.push({ dataLabel: showEle });
                    } else {
                        labelUpdatePropsList.push({
                            dataLabel: ChartDefaultValueFactory.createDataLabels({ showVal: false }),
                        });
                    }
                }
            });
            _set(updateProps, 'dataList', labelUpdatePropsList);
            _set(removeProps, 'dataList', labelRemovePropsList);
        }
        updatePropsList.push(updateProps);
        removePropsList.push(removeProps);
    }

    /**
     * 显示label属性更新
     * @param ser
     * @param commonLabelProps
     * @param updateProps
     * @param removeProps
     */
    private updateSerLabelShowProps(ser, commonLabelProps, updateProps: any, removeProps: any) {
        const { dataLabels, dataList = [] } = ser;
        if (!dataLabels) {
            updateProps.dataLabels = ChartDefaultValueFactory.createDataLabels(commonLabelProps);
        } else {
            updateProps.dataLabels = commonLabelProps;
        }
        const labelUpdatePropsList: any[] = [];
        const labelRemovePropsList: any[] = [];
        dataList.forEach((item: DataListItem) => {
            const { dataLabel } = item;
            if (dataLabel) {
                labelUpdatePropsList.push({ dataLabel: commonLabelProps });
            } else {
                labelUpdatePropsList.push({});
            }
            labelRemovePropsList.push({ dataLabel: { layout: null } });
        });
        _set(updateProps, 'dataList', labelUpdatePropsList);
        _set(removeProps, 'dataList', labelRemovePropsList);
    }

    private createLabelCommonProps(config: IChartLayoutConfig) {
        const showEle = {
            showVal: !!config.showVal,
            showCategoryName: !!config.showCategoryName,
            showSeriesName: !!config.showSeriesName,
            showPercent: !!config.showPercent,
        };
        return { labelPosition: config.labelPos, ...showEle };
    }

    /**
     * 调整子母图，图形大小
     * @param config
     */
    private updateOfLieLayout(config: IChartLayoutConfig) {
        const { charts = [] } = this.chartModel.plotArea;
        const [ofPieChart] = charts;
        if (ofPieChart?.type === SubChartType.OfPieChart) {
            if (config.isSecondPieEqual) {
                ofPieChart.updateProps({ secondPieSize: 100 });
            } else if (config.isSecondPieBigger) {
                ofPieChart.updateProps({ secondPieSize: 125 });
            }
        }
    }

    private updateBarConfig(config: IChartLayoutConfig) {
        const { charts = [] } = this.chartModel.plotArea;
        const [chart] = charts;
        const updateProps: any = {};
        if (!_isNil(config.overlap)) {
            updateProps.overlap = config.overlap;
        }
        if (!_isNil(config.gapWidth)) {
            updateProps.gapWidth = config.gapWidth;
        }
        chart.updateProps(updateProps);
    }
}
